/**
 * Class is responsible for changing views by manipulating URL params.
 */
import _ from 'lodash';
import { RepoItem, ZmCollection } from './autogenerated/ZoomBridge';
import t from './i18n/I18n';
import SaveSettings from './queries/settings/SaveSettings';
import {
  ALL_FILES_OPTION,
  ASSETLIST_PAGE_NUMBER,
  BREADCRUMB_ASSETS,
  COLLECTIONS,
  COLLECTION_ASSETS,
  COLLECTION_ID,
  GLOBAL_SEARCH_INFO,
  IS_SEARCH_NOT_FROM_ASSET_TAB,
  MODE,
  PWD,
  PWD_COLLECTIONS,
  SHOW_FOLDERNAV_VIEW
} from './store/Constants';
import Store from './store/Store';
import { getRemotePathFromBreadcrumb } from './store/StoreUtil';
import { Q, SUBTAB, TABS } from './util/Constants';
import { isMobile } from './util/ScreenUtil';
import { SetterStoreUtil } from './store/setterStoreUtil';
import CollectionUtil from './queries/lightbox/CollectionUtil';
import Authenticator from './protocol/Authenticator';

const debug = require('./debug/debug')('wc:ViewUtil');

export const View = {
  HOME: 'HOME',
  ASSET_LIST: 'ASSET_LIST',
  ASSET_DETAIL: 'ASSET_DETAIL',
  COLLECTION_LIST: 'COLLECTION_LIST',
  COLLECTION_DETAIL_ASSET_LIST: 'COLLECTION_DETAIL_ASSET_LIST',
  COLLECTION_DETAIL_ASSET_DETAIL: 'COLLECTION_DETAIL_ASSET_DETAIL'
};

export default class ViewUtil {
  static URL_TREE = {
    activeTab: ['HOME', 'ASSETS', 'COLLECTIONS'],
    activeSubTab: ['ALL', 'BROWSE', 'SEARCH'],
    ASSETS: [],
    COLLECTIONS: [
      'collectionId', //Collection which is open.
      'collection' //Collection unique object..
    ]
  };

  static switchToTab(tabName, searchView: boolean = false, subTabName: String = SUBTAB.ALL.KEY, resetTab: boolean = false) {
    const { activeSubTab, collection, collectionId, cid, fuid, tv, fnv } = window.ARGS;
    let { pwd_collections } = window.ARGS;
    let args = { activeTab: tabName };

    if (tabName === 'COLLECTIONS') {
      if (!subTabName || subTabName === SUBTAB.NONE.KEY)
        subTabName = activeSubTab;
      args.activeSubTab = !subTabName || subTabName === SUBTAB.NONE.KEY ? SUBTAB.ALL.KEY : subTabName;

      if (collection) {
        args.collection = collection;
      }

      if (resetTab && !collection) {
        let collId = subTabName === SUBTAB.BROWSE.KEY ?
          CollectionUtil.ROOT_COLLECTION_ID : CollectionUtil.ALL_COLLECTION_ID
        Store.put(COLLECTION_ID, collId);
        ViewUtil.setARGS(args); //Have to do temporary set args here to load collections for first time.
        CollectionUtil.loadCollectionsInView(collId);
      }
      else {
        if (collectionId || cid) {
          args.collectionId = collectionId ? collectionId : cid;
          if (fuid) {
            args.fuid = fuid;
          }
          if (!pwd_collections || pwd_collections.length <= 0)
            pwd_collections = [collectionId ? collectionId : cid];
        }
        args = ViewUtil.setCollectionPwd(args, pwd_collections);
      }
    } else if (tabName === 'REPORTING') {
      args.rpt = true;
    }

    if (tabName === 'ASSETS') {
      if (fnv === 0) {
        args.fnv = 0;
      }
      if (tv === 0) {
        args.tv = 0;
      }
      if (searchView) {
        Store.put(IS_SEARCH_NOT_FROM_ASSET_TAB, true);
      }
    }

    ViewUtil.switchToAssetListView();
    ViewUtil.clearThreadIdCommentId();
    if (!searchView && !window.gs.loadFromBackButton) {
      ViewUtil.setGlobalSearchInactive();
    }
    ViewUtil.setPwd(null);
    ViewUtil.setARGS(args, true);

    ViewUtil.setTitle();
  }

  static setGlobalSearchInactive() {
    const globalSearchInfo = Store.get(GLOBAL_SEARCH_INFO);
    if (globalSearchInfo.active) {
      Store.put(GLOBAL_SEARCH_INFO, { active: false, searchString: '' });
    }
  }

  static getMode() {
    const mode = SaveSettings.get(MODE);
    if (mode && mode.toUpperCase() === 'SEARCH') return 'SEARCH';
    if (mode && mode.toUpperCase() === 'FOLDERNAV') return 'FOLDERNAV';
    if (mode && mode.toUpperCase() === 'TREE') return 'TREE';
    return 'SEARCH';
  }

  static getActiveSubTab() {
    return window.ARGS.activeSubTab ? window.ARGS.activeSubTab : SUBTAB.ALL.KEY;
  }

  static getActiveTab() {
    if (ViewUtil.isHomeTabActive()) {
      return TABS.HOME.KEY;
    } else if (ViewUtil.isAssetTabActive()) {
      return TABS.ASSETS.KEY;
    } else if (ViewUtil.isCollectionTabActive()) {
      return TABS.COLLECTIONS.KEY;
    } else {
      return window.ARGS.activeTab;
    }
  }

  static isHomeTabActive() {
    const systemTabs = ['ASSETS', 'COLLECTIONS', 'REPORTING', 'SEARCH', 'SETTINGS', TABS.EXTERNAL_FILES.KEY];
    const mobileTabs = ['ASSETS', 'COLLECTIONS', 'REPORTING', 'SEARCH', 'SETTINGS'];
    return isMobile() ? !_.includes(mobileTabs, window.ARGS.activeTab) : !_.includes(systemTabs, window.ARGS.activeTab);
  }

  static isAssetTabActive() {
    return window.ARGS.activeTab === 'ASSETS';
  }
  static isSettingsTabActive() {
    return window.ARGS.activeTab === 'SETTINGS';
  }

  static isCollectionTabActive() {
    return window.ARGS.activeTab === 'COLLECTIONS';
  }

  static isSeachTabActive() {
    return window.ARGS.activeTab === 'SEARCH';
  }

  static isBrowseExternalTabActive() {
    return window.ARGS.activeTab === TABS.EXTERNAL_FILES.KEY && !isMobile();
  }

  static isBrowseSubTabActive() {
    return window.ARGS.activeSubTab && window.ARGS.activeSubTab === SUBTAB.BROWSE.KEY;
  }

  static isAllSubTabActive() {
    return !window.ARGS.activeSubTab || window.ARGS.activeSubTab === SUBTAB.ALL.KEY;
  }

  static isSearchSubTabActive() {
    return window.ARGS.activeSubTab && window.ARGS.activeSubTab === SUBTAB.SEARCH.KEY;
  }

  static isCollectionListViewActive() {
    return ViewUtil.isCollectionTabActive() && !window.ARGS.collectionId;
  }

  static isCollectionDetailAssetListViewActive() {
    return ViewUtil.isCollectionTabActive() && !!window.ARGS.collectionId && !window.ARGS.fuid;
  }

  static isCollectionDetailAssetDetailViewActive() {
    return ViewUtil.isCollectionTabActive() && !!window.ARGS.collectionId && !!window.ARGS.fuid;
  }

  static isCollectionBrowseTabActive() {
    return ViewUtil.isCollectionTabActive() && ViewUtil.isBrowseSubTabActive();
  }

  static isCollectionAllTabActive() {
    return ViewUtil.isCollectionTabActive() && ViewUtil.isAllSubTabActive();
  }

  static isCollectionSearchTabActive() {
    return ViewUtil.isCollectionTabActive() && ViewUtil.isSearchSubTabActive();
  }

  static isAssetListViewActive() {
    return !window.ARGS.fuid;
  }

  static isAssetAIViewActive() {
    return window.ARGS.ai;
  }

  static isReportingTabActive() {
    return window.ARGS.activeTab === 'REPORTING';
  }

  static isAssetDetailViewActive() {
    return !!window.ARGS.fuid;
  }

  static getFuid() {
    const { fuid } = window.ARGS;
    return fuid ? parseInt(fuid, 10) : null;
  }

  static isDetailsBlockViewActive() {
    return ViewUtil.isAssetTabActive() || !!window.ARGS.detail;
  }

  /**
   * Method will switch to collections views. Where we display the list of all collections.
   */
  static switchToCollections() {
    return ViewUtil.switchToCollectionsAllTab();
  }

  static switchToCollectionsBrowseTab() {
    ViewUtil.switchToTab('COLLECTIONS', false, SUBTAB.BROWSE.KEY);
  }

  static switchToCollectionsAllTab() {
    ViewUtil.switchToTab('COLLECTIONS', false, SUBTAB.ALL.KEY);
  }

  static switchToHomeTab() {
    ViewUtil.switchToTab('HOME');
  }

  static switchToAssetsTab() {
    ViewUtil.switchToTab('ASSETS');
  }

  static switchToAssetsTabFolder(pwd: number[]) {
    SetterStoreUtil.setAllFilesOption(false);
    ViewUtil.setARGS({ activeTab: 'ASSETS', pwd: ViewUtil._pwdArrayToString(pwd) }, true);
    Store.put(PWD, pwd);
  }

  /**
   * Method will switch to collection detail view. Where we display the list of all assets in a collection.
   */

  static switchToBinId(binId, activeSubTab = SUBTAB.BROWSE.KEY, pwd_collections = []) {
    let args = { ...window.ARGS };
    delete args.fuid;
    delete args.pwd;
    delete args.collectionId;
    delete args.type;
    args.activeTab = 'COLLECTIONS';
    args.activeSubTab = activeSubTab == SUBTAB.NONE.KEY ? SUBTAB.BROWSE.KEY : activeSubTab;
    //args.binId = binId;
    args = ViewUtil.setCollectionPwd(args, pwd_collections);
    ViewUtil.setARGS(args, true);
    ViewUtil.setTitle();
  }

  static switchToCollectionId(collectionId, activeSubTab = ViewUtil.getActiveSubTab(), pwd_collections = []) {
    let args = { ...window.ARGS };
    delete args.fuid;
    delete args.pwd;
    delete args.type;
    //delete args.binId;
    args.activeTab = 'COLLECTIONS';
    args.activeSubTab = activeSubTab == SUBTAB.NONE.KEY ? SUBTAB.ALL.KEY : activeSubTab;
    args.collectionId = collectionId;
    if (activeSubTab === SUBTAB.ALL.KEY) {
      pwd_collections = [collectionId];
    }
    args = ViewUtil.setCollectionPwd(args, pwd_collections);
    ViewUtil.setARGS(args, true);
    ViewUtil.setTitle();
  }

  static switchToSetting(currentSetting) {
    let args = { ...window.ARGS };
    args.activeTab = 'SETTINGS';
    delete args.activeSubTab;
    args.currentSetting = currentSetting;
    ViewUtil.setARGS(args, true);
  }

  static setARGS(args, replace = false) {
    const activeTab = args['activeTab'];
    const currentActiveTab = window.ARGS['activeTab'];
    const activeSubTab = args['activeSubTab'];
    const currentActiveSubTab = window.ARGS['activeSubTab'];
    // only replace when param replace isn't in args
    if (!replace) {
      if (activeTab && activeTab !== currentActiveTab) {
        replace = true;
      } else if (activeSubTab && activeSubTab !== currentActiveSubTab) {
        replace = true;
      } else {
        replace = false;
      }
    }
    if (args.pwd === '' || args.pwd_collections === '') {
      //do nothing..
    } else {
      window.setARGS(args, replace);
    }

    window.render();
  }

  static removeArg(name) {
    const args = { ...window.ARGS };
    delete args[name];
    ViewUtil.setARGS(args, true);
  }

  /**
   * Switch to collection add asset form.
   */
  static switchViewToAddAssetsInCollection(collectionId) {
    window.gs.addAssetsForm = true;
    ViewUtil.setARGS({ activeTab: 'COLLECTIONS', activeSubTab: ViewUtil.getActiveSubTab(), collectionId });
    ViewUtil.setTitle();
  }

  static switchToAssetListView() {
    let args = { ...window.ARGS };
    if (!args.fuid) {
      return;
    }

    window.gs.selectedAssets = [];
    delete args.fuid;
    delete args.cId;
    delete args.tId;
    delete args.dialog;
    window.setARGS(args, true);
    window.render();
    ViewUtil.setTitle();
  }

  static switchToAssetAIView(fuid, ai, enforceAssetsTab = false, replace = false) {
    const args = { fuid, ai };
    if (enforceAssetsTab) {
      args.activeTab = 'ASSETS';
      delete args.activeSubTab;
    }
    window.setARGS(args, replace);
    window.render();
    ViewUtil.setTitle();
  }

  static switchToAssetDetailView(fuid, enforceAssetsTab = false, replace = false) {
    const args = { fuid };
    if (enforceAssetsTab) {
      args.activeTab = 'ASSETS';
      delete args.activeSubTab;
    }
    window.setARGS(args, replace);
    window.render();
    ViewUtil.setTitle();
  }

  static switchToAdvancedSearch(searchString: string) {
    const args = { activeTab: 'SEARCH' };
    if (searchString) {
      args.q = JSON.stringify({ find: searchString });
    }
    ViewUtil.setARGS(args, true);
    ViewUtil.setTitle();
  }

  static isAdvancedSearchTabActive() {
    return window.ARGS.activeTab === 'SEARCH';
  }

  /**
   * Method tells, if URL has direct collection link property or not.
   * @return {boolean}
   */
  static isDirectLinkToCollection() {
    const args = window.ARGS;
    if (args) {
      const privateLinkId = args['collection'];
      const collectionId = args['collectionId'];
      return privateLinkId && collectionId && parseInt(collectionId, 10) > 0;
    }
    return false;
  }

  static isSharedLinkUser() {
    return window.gs.isSharedLinkUser ? true : false;
  }

  /**
   * Returns the collection id from window.ARGS
   * @return {number}
   */
  static getCollectionIdFromArgs() {
    const args = window.ARGS;
    if (args) {
      const collectionId = args['collectionId'];
      if (collectionId) {
        const cId = parseInt(collectionId, 10);
        if (!isNaN(cId))
          return cId;
      }
    }
    return null;
  }

  /**
   * Returns the privateLinkId from window.ARGS
   * @return {string}
   */
  static getPrivateLinkId() {
    const args = window.ARGS;
    if (args) {
      const privateLinkId = args['collection'];
      if (privateLinkId)
        return privateLinkId;
    }
    return null;
  }

  /**
   * It is assumed that, you call this method after calling isDirectLinkToCollection.
   * Method will return two items in array. First is privateLinkId(string) and second is collectionId(number)
   * @return {Array}
   */
  static getLinkDetails() {
    const args = window.ARGS;
    if (args) {
      const privateLinkId = args['collection'];
      const collectionId = parseInt(args['collectionId'], 10);
      return [privateLinkId, collectionId];
    }
    return undefined;
  }

  static deleteCollectionLinkId() {
    const args = window.ARGS;
    if (args) {
      delete args.collection;
    }
    ViewUtil.setARGS(args);
  }

  static showBottomLeftTip(message, dismissDelay = 0) {
    window.gs.bottomLeftTip = { message, dismissDelay };
    window.render();
  }

  static clearThreadIdCommentId() {
    let args = { ...window.ARGS };
    delete args.tId;
    delete args.cId;
    window.setARGS(args, true);
  }

  static reloadWindowToCollectionId(collectionId) {
    window.setARGS({ activeTab: 'COLLECTIONS', activeSubTab: SUBTAB.ALL.KEY, collectionId: collectionId }, true);
    location.reload();
  }

  static setDialog(dialog) {
    let args = { ...window.ARGS };
    let replace = false;
    if (dialog) {
      args.dialog = dialog;
    } else {
      delete args.dialog;
      replace = true;
    }

    ViewUtil.setARGS(args, replace);
  }

  static getDialog() {
    return window.ARGS.dialog;
  }

  static getActiveView() {
    if (ViewUtil.isAssetTabActive() && ViewUtil.isAssetListViewActive()) {
      return View.ASSET_LIST;
    }
    if (ViewUtil.isAssetTabActive() && ViewUtil.isAssetDetailViewActive()) {
      return View.ASSET_DETAIL;
    }
    if (ViewUtil.isCollectionListViewActive()) {
      return View.COLLECTION_LIST;
    }
    if (ViewUtil.isCollectionDetailAssetListViewActive()) {
      return View.COLLECTION_DETAIL_ASSET_LIST;
    }
    if (ViewUtil.isCollectionDetailAssetDetailViewActive()) {
      return View.COLLECTION_DETAIL_ASSET_DETAIL;
    }

    return View.HOME;
  }

  static getReportId() {
    const { reportId } = window.ARGS;
    return reportId ? parseInt(reportId, 10) : null;
  }

  static setTitle() {
    const args = window.ARGS || {};
    const { activeTab, activeSubTab } = args;
    const fuid = ViewUtil.getFuid();
    const collectionId = ViewUtil.getCollectionIdFromArgs();
    const tokens = [];

    if (activeTab === 'ASSETS') {
      if (fuid) {
        const asset = window.gs.selectedAssets.length === 1 ? window.gs.selectedAssets[0] : null;
        if (asset && asset.getFuid() === fuid) {
          tokens.push(asset.getName());
        }
      } else {
        tokens.push(t('ASSETS'));
      }
    } else if (activeTab === 'COLLECTIONS') {
      if (collectionId) {
        const collections = Store.get(COLLECTIONS) || [];
        const collection: ZmCollection = collections.find((c) => c.getCollectionId() === collectionId);
        if (collection) {
          if (fuid) {
            const asset: RepoItem = Store.get(COLLECTION_ASSETS).find((a) => a.getFuid() === fuid);
            if (asset) {
              tokens.push(asset.getName());
            }
          }
          tokens.push(collection.getName());
        }
      } else {
        tokens.push(t('COLLECTIONS'));
        tokens.push(activeSubTab);
      }
    } else if (activeTab === 'SEARCH') {
      tokens.push(t('SEARCH'));
    } else if (activeTab === 'HOME') {
      tokens.push(t('HOME'));
    } else if (activeTab === TABS.EXTERNAL_FILES.KEY) {
      tokens.push(t(TABS.EXTERNAL_FILES.KEY));
    }

    tokens.push(window.gs.companyName);

    const title = tokens.join(' - ');
    debug('[setTitle()] updating page title to ', title);
    document.title = title;
  }

  static setAdvancedSearchQuery(data: Object) {
    Store.put(Q, data);
  }

  static getAdvancedSearchQuery() {
    try {
      return Store.get(Q);
    } catch (err) {
      return null;
    }
  }

  static getCollectionPwd() {
    return ViewUtil.getCollectionPwdFromArgs();
  }

  static getCollectionPwdFromStore() {
    try {
      return Store.get(PWD_COLLECTIONS) || [];
    } catch (err) {
      return [];
    }
  }

  static getCollectionPwdFromArgs(args: ?Object): number[] {
    args = args || window.ARGS;
    try {
      return _.compact(ViewUtil._pwdStringToArray(args.pwd_collections || '').map((s) => parseInt(s, 10)));
    } catch (err) {
      return [];
    }
  }

  static setCollectionPwd(args, pwd_collections: number[]) {
    debug('[setCollectionPwd()]', pwd_collections);
    if (pwd_collections && pwd_collections.length > 0) {
      args.pwd_collections = ViewUtil._pwdArrayToString(pwd_collections);
      Store.put(PWD_COLLECTIONS, pwd_collections);
    } else {
      delete args.pwd_collections;
      Store.put(PWD_COLLECTIONS, []);
    }
    return args;
  }

  static setCollectionPwdAndUpdate(pwd_collections: number[]) {
    debug('[setCollectionPwdAndUpdate()]', pwd_collections);
    if (pwd_collections && pwd_collections.length > 0) {
      ViewUtil.setARGS({ pwd_collections: ViewUtil._pwdArrayToString(pwd_collections) });
    } else {
      const args = { ...window.ARGS };
      delete args.pwd_collections;
      ViewUtil.setARGS(args, true);
    }

    Store.put(PWD_COLLECTIONS, pwd_collections);
  }

  static pushCollectionPwd(collectionId: number, append: boolean) {
    let pwd_collections;
    if (append) {
      const currentPwd = ViewUtil.getCollectionPwd() || [];
      pwd_collections = currentPwd.concat(collectionId);
    } else {
      pwd_collections = [collectionId];
    }

    ViewUtil.setCollectionPwdAndUpdate(pwd_collections);
  }

  static popCollectionPwd(index: number) {
    const pwd_collections = ViewUtil.getCollectionPwd().slice(0, index + 1);
    ViewUtil.setCollectionPwdAndUpdate(pwd_collections);
  }

  static getPwd(args: ?Object): number[] {
    args = args || window.ARGS;
    try {
      return _.compact(ViewUtil._pwdStringToArray(args.pwd || '').map((s) => parseInt(s, 10)));
    } catch (err) {
      return [];
    }
  }

  static setPwd(pwd: number[]) {
    debug('[setPwd( %O )]', pwd);
    Store.put(ASSETLIST_PAGE_NUMBER, 0);
    if (pwd) {
      ViewUtil.setARGS({ pwd: ViewUtil._pwdArrayToString(pwd) });
    } else {
      const args = { ...window.ARGS };
      delete args.pwd;
      ViewUtil.setARGS(args, true);
    }

    Store.put(PWD, ViewUtil.getPwd());
  }

  static pushPwd(fuid: number, append: boolean) {
    let pwd;
    if (append) {
      const currentPwd = ViewUtil.getPwd() || [];
      pwd = currentPwd.concat(fuid);
    } else {
      pwd = [fuid];
    }

    ViewUtil.setPwd(pwd);
  }

  static popPwd(index: number) {
    const pwd = ViewUtil.getPwd().slice(0, index + 1);
    ViewUtil.setPwd(pwd);
  }

  static _pwdArrayToString(pwd: number[]) {
    return pwd.join('.');
  }

  static _pwdStringToArray(pwd: string) {
    return pwd.split('.');
  }

  /**
   * Check Asset tab Show root Directory (Projects Dir.)
   * @return {Boolean}
   */
  static isAssetRootDir() {
    if (this.isAssetTabActive()) {
      if (Store.get(PWD).length === 0) {
        return false;
      }
    }
    return true;
  }

  static setTreeView(treeView: boolean) {
    const args = { ...window.ARGS };
    if (treeView) {
      delete args.tv;
    } else {
      args.tv = 0;
    }
    ViewUtil.setARGS(args, true);
  }

  static setFolderNavView(folderNavView: boolean) {
    const args = { ...window.ARGS };
    if (folderNavView) {
      delete args.fnv;
      delete args.tv;
    } else {
      args.fnv = 0;
    }
    console.log(args);
    ViewUtil.setARGS(args, true);
  }

  static getTreeView() {
    const args = { ...window.ARGS };
    if (args.tv) {
      return false;
    } else {
      return true;
    }
  }

  static isAllFileSearchMode() {
    if (!Store.get(SHOW_FOLDERNAV_VIEW)) return true;
    if (Store.get(ALL_FILES_OPTION)) return true;
    const globalSearchMode = Store.get(GLOBAL_SEARCH_INFO).active;
    if (globalSearchMode) return true;
    return false;
  }

  static getFolderNavView() {
    const args = { ...window.ARGS };
    if (args.fnv) {
      return false;
    } else {
      return true;
    }
  }
}

//Get All Params from URL like &THEME=DARK
export const getParamsFromUrl = function (url) {
  var queryString = url ? url.split('?')[1] : window.location.search.slice(1);
  var obj = {};
  if (queryString) {
    queryString = queryString.split('#')[0];
    var arr = queryString.split('&');
    for (var i = 0; i < arr.length; i++) {
      var a = arr[i].split('=');
      var paramName = a[0];
      var paramValue = typeof a[1] === 'undefined' ? true : a[1];
      paramName = paramName.toLowerCase();
      if (typeof paramValue === 'string') paramValue = paramValue.toLowerCase();
      if (paramName.match(/\[(\d+)?\]$/)) {
        var key = paramName.replace(/\[(\d+)?\]/, '');
        if (!obj[key]) obj[key] = [];
        if (paramName.match(/\[\d+\]$/)) {
          var index = /\[(\d+)\]/.exec(paramName)[1];
          obj[key][index] = paramValue;
        } else {
          obj[key].push(paramValue);
        }
      } else {
        if (!obj[paramName]) {
          obj[paramName] = paramValue;
        } else if (obj[paramName] && typeof obj[paramName] === 'string') {
          obj[paramName] = [obj[paramName]];
          obj[paramName].push(paramValue);
        } else {
          obj[paramName].push(paramValue);
        }
      }
    }
  }
  return obj;
};

//For Get Search mode or AllFile view Mode


export const getUploadPath = function (asset?: RepoItem) {
  let projectName = '';
  let uploadPath = '';
  const breadCrumAssets = Store.get(BREADCRUMB_ASSETS);
  const isFolderNavView = Store.get(SHOW_FOLDERNAV_VIEW);
  if (isFolderNavView && breadCrumAssets.length && !asset) {
    const projectRepoItem: RepoItem = _.head(breadCrumAssets);
    if (projectRepoItem) {
      projectName = projectRepoItem.getName();
      uploadPath = getRemotePathFromBreadcrumb();
    }
  } else if (isFolderNavView && asset) {
    projectName = asset.getName();
    uploadPath = asset.getRemotePath() + '/';
  }
  return { projectName, uploadPath };
};

export const getWindowPixelHeight = function () {
  return window.devicePixelRatio && window.devicePixelRatio > 1 ? window.devicePixelRatio * window.innerHeight : window.innerHeight;
}

export const getWindowPixelWidth = function () {
  return window.devicePixelRatio && window.devicePixelRatio > 1 ? window.devicePixelRatio * window.innerWidth : window.innerWidth;
}

export const getMaxWindowPixelDimension = function () {
  return window.devicePixelRatio && window.devicePixelRatio > 1 ? window.devicePixelRatio * Math.max(window.innerHeight, window.innerWidth) : Math.max(window.innerHeight, window.innerWidth);
}
